/**
 * Frontier hints on the watercolor atlas (worldMapFrontier.ts): from a place, a dashed road stub
 * runs into a soft fog bank with a "?" and the road's name. Decorative and unnamed: the draft
 * zone beyond is not a place on the atlas yet.
 */
import React from 'react';
import type { FrontierRoad } from './worldMapFrontier';

interface FrontierAnchor { id: string; x: number; y: number }

export const WatercolorFrontierLayer: React.FC<{ roads: readonly FrontierRoad[]; anchors: readonly FrontierAnchor[] }> = ({ roads, anchors }) => {
  const byId = new Map(anchors.map(anchor => [anchor.id, anchor]));
  const drawn = roads.flatMap(road => {
    const from = byId.get(road.from);
    return from && road.placement ? [{ road, from, placement: road.placement }] : [];
  });
  if (!drawn.length) return null;
  return (
    <g className="watercolor-map-frontier" aria-hidden="true">
      <defs>
        <radialGradient id="world-map-frontier-bank">
          <stop offset="0" stopColor="#efe4c8" stopOpacity="0.95" />
          <stop offset="0.6" stopColor="#e4d6b6" stopOpacity="0.7" />
          <stop offset="1" stopColor="#e4d6b6" stopOpacity="0" />
        </radialGradient>
      </defs>
      {drawn.map(({ road, from, placement }) => {
        const x = from.x + placement.dx, y = from.y + placement.dy;
        // Bow the stub a little so it reads as a road, not a pointer.
        const cx = (from.x + x) / 2 - placement.dy * 0.18, cy = (from.y + y) / 2 + placement.dx * 0.18;
        return (
          <g key={road.key} className="watercolor-map-frontier-road" data-frontier={road.key}>
            <path className="watercolor-map-frontier-stub" d={`M ${from.x} ${from.y} Q ${cx} ${cy} ${x} ${y}`} />
            <ellipse cx={x} cy={y} rx={88} ry={62} fill="url(#world-map-frontier-bank)" />
            <text className="watercolor-map-frontier-mark" x={x} y={y + 11} textAnchor="middle">?</text>
            <text className="watercolor-map-frontier-label" x={x} y={y + 40} textAnchor="middle">{placement.road}</text>
          </g>
        );
      })}
    </g>
  );
};
